import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close, DragIndicator } from '@mui/icons-material';
import { motion, useDragControls } from 'framer-motion';

interface DraggableDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  fullWidth?: boolean;
  showDragHandle?: boolean;
}

const DraggableDialog: React.FC<DraggableDialogProps> = ({
  open,
  onClose,
  title,
  children,
  actions,
  maxWidth = 'sm',
  fullWidth = true,
  showDragHandle = false,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const dragControls = useDragControls();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  // On mobile, use full screen dialog without dragging
  if (isMobile) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen
        PaperProps={{
          sx: {
            bgcolor: 'rgba(18, 18, 18, 0.98)',
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
            py: 1.5,
            px: 2,
          }}
        >
          <Typography variant="h6" component="span" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
            {title}
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
              },
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 2, px: 2 }}>
          {children}
        </DialogContent>

        {actions && (
          <DialogActions sx={{ px: 2, py: 2, borderTop: '1px solid rgba(255, 255, 255, 0.06)' }}>
            {actions}
          </DialogActions>
        )}
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      PaperProps={{
        component: motion.div,
        drag: true,
        dragControls: dragControls,
        dragListener: false,
        dragMomentum: false,
        dragElastic: 0.1,
        dragConstraints: {
          top: -200,
          left: -400,
          right: 400,
          bottom: 200,
        },
        onDragStart: handleDragStart,
        onDragEnd: handleDragEnd,
        initial: { opacity: 0, scale: 0.95 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.95 },
        transition: { duration: 0.2 },
        sx: {
          cursor: isDragging ? 'grabbing' : 'default',
          overflow: 'visible',
        },
      } as any}
    >
      <DialogTitle
        onPointerDown={(e) => dragControls.start(e)}
        sx={{
          cursor: isDragging ? 'grabbing' : 'grab',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
          userSelect: 'none',
          pr: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {showDragHandle && (
            <DragIndicator
              sx={{
                fontSize: 20,
                color: 'text.secondary',
                opacity: 0.5,
              }}
            />
          )}
          <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
            },
          }}
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {children}
      </DialogContent>

      {actions && (
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid rgba(255, 255, 255, 0.06)' }}>
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default DraggableDialog;
